import { useEffect, useMemo, useState } from 'react';
import { PatientRoomControllerApi } from '../api/generated';
import type { PatientRoomResponse } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { getErrorMessage } from '../utils/errorMessageUtils';

export interface UsePatientRoomsResult {
  // The location's patient rooms, one bed each, with the properties each offers.
  // Their number is the ceiling on admitted patients in any one week. Undefined
  // until this location's rooms have settled, and if the fetch failed: a
  // consumer must then judge nothing against them rather than against an empty
  // list.
  patientRooms: readonly PatientRoomResponse[] | undefined;
  loadError: string | null;
}

const PATIENT_ROOMS_LOAD_ERROR = 'Failed to load patient rooms';

// The rooms are stored with the location they were read for, so a location
// switch cannot leave the previous location's rooms standing while the new fetch is
// still in flight — the KPI would otherwise claim an overbooking that belongs
// to somewhere else. It also means no state has to be cleared on the way out,
// which is what keeps the effect below free of a synchronous setState.
interface LoadedRooms {
  readonly locationId: string;
  readonly rooms: readonly PatientRoomResponse[];
}

export function usePatientRooms(locationId: string | null): UsePatientRoomsResult {
  const [loaded, setLoaded] = useState<LoadedRooms | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const api = useMemo(() => new PatientRoomControllerApi(createApiConfiguration()), []);

  // The request is issued inside the effect and the state set from its callbacks:
  // `react-hooks/set-state-in-effect` traces setState through a synchronous call
  // graph, so the same work behind a useCallback would be flagged.
  useEffect(() => {
    if (!locationId) return;
    let isCurrent = true;
    api
      .listPatientRooms({ locationId })
      .then((response) => {
        if (!isCurrent) return;
        setLoaded({ locationId, rooms: response.data });
        setLoadError(null);
      })
      .catch((e: unknown) => {
        if (!isCurrent) return;
        // No rooms are recorded, so the bed chip reports demand without claiming
        // a shortage. Surfaced as state rather than an error dialog, which would
        // interrupt the year plan over a KPI threshold.
        setLoadError(getErrorMessage(e, PATIENT_ROOMS_LOAD_ERROR));
      });
    return () => {
      isCurrent = false;
    };
  }, [api, locationId]);

  return {
    patientRooms: loaded?.locationId === locationId ? loaded.rooms : undefined,
    loadError
  };
}
